import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { deleteTrip, restoreTrip } from '../../../data/commands/shopping';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import { Button, Money, ScreenHeader, showToast } from '../../../ui';
import { HistoryDisclosure } from '../../../ui/HistoryDisclosure';
import { dayHeading, formatWhen } from '../../spending/format';
import { useGoBack, useToday } from '../../spending/hooks';
import { copy, TRIPS_BACK } from '../copy';
import { lineUnitPrice, quantityText } from '../format';
import { useTrip, useTripHistory, useTripLines } from '../hooks';
import { CARD, FOCUS, LABEL, QUIET_LINK, SCREEN } from '../styles';

/** One trip: items, quantities and prices, the linked expense, and its history (spec §6). */
export function TripDetailScreen() {
  const { id = '' } = useParams();
  const env = useCommandEnv();
  const usable = useUsable();
  const goBack = useGoBack('/m/shopping/trips');
  const today = useToday();
  const trip = useTrip(id);
  const lines = useTripLines(id) ?? [];
  const historyRows = useTripHistory(id) ?? [];
  const [armed, setArmed] = useState(false);

  if (trip === undefined || trip === null) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.tripTitle} back={TRIPS_BACK} />
        <p className="text-muted">{trip === undefined ? copy.loading : copy.tripNotFound}</p>
      </main>
    );
  }
  const deleted = trip.deletedAt !== null;

  async function onDelete() {
    if (!armed) {
      setArmed(true);
      return;
    }
    const r = await deleteTrip(env, id);
    if (!r.ok) return;
    showToast({
      message: copy.tripDeleted,
      action: { label: copy.undo, onPress: () => void restoreTrip(env, id) },
    });
    goBack();
  }

  async function onRestore() {
    await restoreTrip(env, id);
    showToast({ message: copy.tripRestored });
  }

  return (
    <main className={SCREEN}>
      <ScreenHeader title={trip.store ?? copy.tripTitle} back={TRIPS_BACK} />
      <header className={CARD}>
        <p className={LABEL}>
          {dayHeading(trip.date, today)}
          {trip.store ? ` · ${trip.store}` : ''}
        </p>
        <Money
          minor={trip.totalMinor}
          animate={false}
          className="block text-[40px] leading-tight text-fg"
        />
        <p className="text-sm text-muted">{copy.items(lines.length)}</p>
        {deleted ? (
          <p role="status" className="mt-2 text-sm text-muted">
            {copy.deletedTrip}
          </p>
        ) : null}
      </header>

      <ul
        aria-label={copy.tripTitle}
        className={`${CARD} flex flex-col divide-y divide-divider py-1`}
      >
        {lines.map((l) => {
          const qty = quantityText(l.quantity, l.unit);
          const unitPrice =
            l.priceMinor != null ? lineUnitPrice(l.priceMinor, l.quantity, l.unit) : null;
          return (
            <li key={l.id}>
              <Link
                to={`/m/shopping/prices/${encodeURIComponent(l.normalizedName)}`}
                className={`flex min-h-12 items-center justify-between gap-3 rounded-xl py-2 ${FOCUS}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-fg">{l.name}</span>
                  {qty || unitPrice ? (
                    <span className="block truncate text-xs text-muted">
                      {[qty, unitPrice].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </span>
                {l.priceMinor != null ? (
                  <Money
                    minor={l.priceMinor}
                    cents="auto"
                    animate={false}
                    className="shrink-0 text-fg"
                  />
                ) : (
                  <span className="shrink-0 text-sm text-muted">{copy.noPrice}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      {trip.expenseId && usable('spending') ? (
        <Link to={`/m/spending/entries/${trip.expenseId}`} className={`${QUIET_LINK} self-start`}>
          {copy.viewExpense}
        </Link>
      ) : !trip.expenseId ? (
        <p className="text-sm text-muted">{copy.noExpense}</p>
      ) : null}

      <div className="flex gap-2">
        {deleted ? (
          <Button variant="primary" onClick={() => void onRestore()}>
            {copy.restore}
          </Button>
        ) : (
          <Button variant="destructive" onClick={() => void onDelete()}>
            {armed ? copy.confirmDelete : copy.delete}
          </Button>
        )}
      </div>

      {historyRows.length > 0 ? (
        <HistoryDisclosure>
          <section aria-label={copy.history} className={CARD}>
            <h2 className={`mb-3 ${LABEL}`}>{copy.history}</h2>
            <ol className="flex flex-col gap-3 border-l border-divider pl-4">
              {historyRows.map((h) => (
                <li key={h.id}>
                  <p className="text-xs text-muted">
                    <time dateTime={h.at}>{formatWhen(h.at, env.tz)}</time>
                  </p>
                  <p className="text-fg">{copy.historyAction(h.action)}</p>
                </li>
              ))}
            </ol>
          </section>
        </HistoryDisclosure>
      ) : null}
    </main>
  );
}
